<template>
  <div class="w-full flex-1 min-h-0 flex flex-col">
    <!-- Top bar (same language as the stock details page) -->
    <header class="shrink-0 bg-surface/90 backdrop-blur-md border-b border-line-card">
      <div class="h-14 px-4 md:px-6 flex items-center gap-3">
        <img src="https://www.daytradercanada.com/wp-content/themes/daytrader/public/images/svg/logo.svg"
          alt="DayTrader Canada" class="h-8 sm:h-9 dark:hidden" />
        <!-- Same logo with a light wordmark for the dark theme. -->
        <img src="../../assets/logo-daytrader-dark.svg" alt="DayTrader Canada" class="h-8 sm:h-9 hidden dark:block" />
        <span class="hidden sm:block h-5 w-px bg-line-card"></span>
        <span class="hidden sm:block text-[14px] font-semibold text-ink">{{ $t("Stock screener") }}</span>
        <div class="ml-auto flex items-center gap-2">
          <ThemeToggle />
          <select @change="changeLanguage" :value="language" :aria-label="$t('Select Language')" class="qp-select">
            <option value="fr">Français</option>
            <option value="en">English</option>
          </select>
          <button type="button" @click="feedbackModal = true" class="qp-btn qp-btn-dark"
            :title="$t('Feedback & Suggestions')">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M2.5 3.5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v6.5a1 1 0 0 1-1 1H7l-3 2.5V11H3.5a1 1 0 0 1-1-1z"
                stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" />
              <path d="M5.5 6h5M5.5 8h3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
            </svg>
            <span class="hidden sm:inline">{{ $t("Feedback & Suggestions") }}</span>
          </button>
        </div>
      </div>
    </header>

    <div class="w-full flex-1 min-h-0 flex">
    <div class="min-w-0 flex-1 min-h-0 flex flex-col gap-4 px-3 sm:px-4 md:px-6 py-4">
      <!-- Filters + scan controls in one compact card -->
      <section class="qp-card shrink-0">
        <!-- One toolbar row: filter groups on the left, scan controls on the right. -->
        <div class="px-4 sm:px-5 py-3 flex flex-wrap items-center gap-x-3 gap-y-2">
          <template v-if="showFilter">
            <div class="qp-seg" role="tablist" :aria-label="$t('Filters')">
              <button v-for="item in scannerList" :key="item.name" type="button" role="tab"
                :aria-selected="item.name == selectedScanner ? 'true' : 'false'"
                :class="{ 'is-active': item.name == selectedScanner }" @click="handleSelectedScanner(item)">
                {{ $t(item.name) }}
                <span v-if="filterCounts[item.name]" class="qp-count">{{ filterCounts[item.name] }}</span>
              </button>
            </div>
            <button v-if="activeFilterCount" type="button" @click="handleResetFilter"
              class="qp-btn qp-btn-ghost !px-0 w-[34px]" :title="`${$t('Reset Filter(s)')} (${activeFilterCount})`"
              :aria-label="`${$t('Reset Filter(s)')} (${activeFilterCount})`">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M2.5 8a5.5 5.5 0 1 0 1.6-3.9M2.5 2.5v2.8h2.8" stroke="currentColor" stroke-width="1.5"
                  stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
          </template>

          <!-- Saved scans (stored in this browser) -->
          <div class="relative" data-menu-root>
            <button type="button" class="qp-btn qp-btn-ghost" :class="{ '!px-0 w-[34px]': !savedScans.length }"
              :title="$t('Saved scans')" :aria-label="$t('Saved scans')" aria-haspopup="true"
              :aria-expanded="openMenu === 'presets' ? 'true' : 'false'" @click="toggleMenu('presets', $event)">
              <svg width="14" height="15" viewBox="0 0 14 16" fill="none" aria-hidden="true">
                <path d="M2 2.5A1.5 1.5 0 0 1 3.5 1h7A1.5 1.5 0 0 1 12 2.5V15l-5-3.2L2 15z" stroke="currentColor"
                  stroke-width="1.4" stroke-linejoin="round" />
              </svg>
              <span v-if="savedScans.length" class="qp-num">{{ savedScans.length }}</span>
            </button>
            <div v-if="openMenu === 'presets'" class="qp-menu w-[18rem] left-0 top-[calc(100%+6px)]"
              @keydown="onMenuKeydown">
              <p class="px-2.5 pt-1.5 pb-1 qp-label">{{ $t("Saved scans") }}</p>
              <p v-if="!savedScans.length" class="px-2.5 py-1.5 text-[12px] text-muted">
                {{ $t("saved-scans-empty") }}
              </p>
              <div v-for="(preset, i) in savedScans" :key="preset.name" class="flex items-center gap-1">
                <button type="button" class="qp-menu-item flex-1 min-w-0" @click="applyPreset(preset)">
                  <span class="truncate">{{ preset.name }}</span>
                  <span class="ml-auto shrink-0 text-[11px] text-faint">{{ $t(presetScanLabel(preset)) }}</span>
                </button>
                <button type="button" class="qp-menu-item !px-2 text-faint hover:!text-down"
                  :aria-label="`${$t('Delete')} ${preset.name}`" :title="$t('Delete')" @click="deletePreset(i)">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                    <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" stroke="currentColor" stroke-width="1.5"
                      stroke-linecap="round" />
                  </svg>
                </button>
              </div>
              <form class="mt-1 pt-2 px-1 pb-1 border-t border-line-soft flex items-center gap-1.5"
                @submit.prevent="savePreset">
                <div class="qp-input flex-1">
                  <input v-model="presetName" type="text" maxlength="40" class="qp-input-field"
                    :placeholder="$t('Name this scan')" :aria-label="$t('Name this scan')" />
                </div>
                <button type="submit" class="qp-btn qp-btn-primary !h-8" :disabled="!presetName.trim()">
                  {{ $t("Save") }}
                </button>
              </form>
            </div>
          </div>

          <div class="ml-auto max-w-full min-w-0 flex flex-wrap items-center justify-end gap-2">
            <select :value="selectedSelector" @change="handleScanSelector($event.target.value)"
              :aria-label="$t('Scan type')" :title="$t('Scan type')" class="qp-select min-w-[13rem]">
              <optgroup v-for="group in scanGroups" :key="group.label" :label="$t(group.label)">
                <option v-for="item in group.scans" :key="item" :value="item">{{ $t(item) }}</option>
              </optgroup>
            </select>
            <!-- The universe has no history: it is always the latest snapshot of each stock. -->
            <div v-if="!isUniverse" class="qp-input w-[140px]" :title="$t('Scan date')">
              <date-picker :lang="language" format="MM/DD/YYYY" :value="selectedDate" @input="onScanDateChange"
                :clearable="false" :disabled-date="isDisabledScanDate" :shortcuts="scanDateShortcuts"
                input-class="qp-date-field" :input-attr="{ 'aria-label': $t('Scan date') }"></date-picker>
            </div>
            <button type="button" @click="showFilter = !showFilter" class="qp-btn qp-btn-ghost"
              :title="showFilter ? $t('Hide Filters') : $t('Show Filters')"
              :aria-expanded="showFilter ? 'true' : 'false'">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M2 4h12M4.5 8h7M7 12h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              </svg>
              <span class="hidden 2xl:inline">{{ showFilter ? $t("Hide Filters") : $t("Show Filters") }}</span>
              <span v-if="!showFilter && activeFilterCount" class="qp-count !ml-0">{{ activeFilterCount }}</span>
            </button>
            <button type="button" @click="runScan()" :disabled="scanLoader" class="qp-btn qp-btn-primary"
              :class="{ 'qp-btn-pulse': filtersDirty }"
              :title="filtersDirty ? $t('filters-changed-hint') : $t('Scan Result')">
              <svg width="11" height="13" viewBox="0 0 14 17" fill="none" aria-hidden="true">
                <path
                  d="M13 6.76795C14.3333 7.53775 14.3333 9.46225 13 10.2321L3.25 15.8612C1.91667 16.631 0.249999 15.6688 0.249999 14.1292L0.25 2.87084C0.25 1.33123 1.91667 0.368983 3.25 1.13878L13 6.76795Z"
                  fill="currentColor" />
              </svg>
              {{ scanLoader ? $t("Scanning Result") : $t("Scan Result") }}
            </button>
          </div>
        </div>

        <!-- Filters edited since the last scan -->
        <div v-if="filtersDirty && !scanLoader" role="status"
          class="mx-4 sm:mx-5 mb-3 -mt-1 flex items-center gap-2 rounded-lg bg-notice-bg px-3 py-1.5 text-[12px] text-notice">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.4" />
            <path d="M8 4.5v4M8 11h.01" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          </svg>
          <span>{{ $t("filters-changed-hint") }}</span>
          <button type="button" class="ml-auto font-semibold underline" @click="runScan()">{{ $t("Run scan") }}</button>
        </div>

        <!-- The universe matched more stocks than one request returns (largest market caps come first) -->
        <div v-else-if="isUniverse && dataResult.length >= RESULT_CAP && !scanLoader" role="status"
          class="mx-4 sm:mx-5 mb-3 -mt-1 flex items-center gap-2 rounded-lg bg-info-bg px-3 py-1.5 text-[12px] text-info">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.4" />
            <path d="M8 7.5v4M8 5h.01" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          </svg>
          <span>{{ $t("universe-cap-hint", { count: resultCapLabel }) }}</span>
        </div>

        <div v-if="showFilter" class="px-4 sm:px-5 pb-4" @keydown.enter="onFilterEnter">
          <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 2xl:grid-cols-8 gap-x-3 gap-y-3">
            <div v-for="item in currentSelects" :key="item.id" class="flex flex-col gap-1 min-w-0"
              :class="{ 'justify-end': item.isCheckbox, 'col-span-2': item.isDate && item.currentVal === BETWEEN }">
              <label v-if="!item.isCheckbox" :for="`filter-${item.key}`" class="qp-field-label"
                :class="{ 'cursor-help': item.def }" :title="filterTitle(item)">
                {{ $t(item.label) }}
              </label>

              <!-- Dropdown -->
              <div v-if="item.isDropdown" class="qp-input" :class="{ 'is-open': openMenu === item.id }"
                data-menu-root>
                <button :id="`filter-${item.key}`" type="button" :title="$t(item.currentVal)"
                  aria-haspopup="listbox" :aria-expanded="openMenu === item.id ? 'true' : 'false'"
                  @click="toggleMenu(item.id, $event)"
                  class="flex-1 min-w-0 h-full pl-2.5 pr-2 flex items-center justify-between gap-1 text-left rounded-lg">
                  <span class="truncate">{{ $t(item.currentVal) }}</span>
                  <svg width="10" height="6" viewBox="0 0 10 6" fill="none" class="shrink-0 text-faint" aria-hidden="true">
                    <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </button>
                <div v-if="openMenu === item.id" role="listbox" :aria-label="$t(item.label)"
                  :class="item.key == 'exchange' ? 'w-[280px]' : 'w-full min-w-[200px]'"
                  class="qp-menu left-0 top-[calc(100%+4px)] max-h-[300px] overflow-y-auto" @keydown="onMenuKeydown">
                  <button v-for="val in item.list" :key="val.key" type="button" role="option"
                    :aria-selected="isOptionSelected(item, val) ? 'true' : 'false'"
                    @click="handleSelectedValue(val, item.id)" class="qp-menu-item w-full text-left"
                    :class="{ 'is-active': isOptionSelected(item, val) }">
                    {{ $t(val.value) }}
                  </button>
                </div>
              </div>

              <!-- Checkbox -->
              <div v-else-if="item.isCheckbox" class="h-8 flex items-center">
                <button type="button" role="checkbox" :aria-checked="item.value ? 'true' : 'false'"
                  :title="item.def ? filterTitle(item) : null" @click="item.value = !item.value"
                  class="flex items-center gap-2 text-[13px] text-ink-2 hover:text-ink">
                  <span class="qp-check" :class="{ 'is-checked': item.value }">
                    <svg v-if="item.value" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      stroke-width="4" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  </span>
                  {{ $t(item.label) }}
                </button>
              </div>

              <!-- Text -->
              <div v-else-if="item.isText" class="qp-input">
                <input :id="`filter-${item.key}`" v-model="item.value" type="text" class="qp-input-field uppercase"
                  autocomplete="off" spellcheck="false" />
                <kbd v-if="item.key === 'symbol' && !item.value" :title="$t('Press / to jump here')"
                  class="self-center mr-2 px-1.5 rounded border border-line bg-surface-2 text-[11px] leading-[18px] text-faint font-sans">/</kbd>
              </div>

              <!-- Comparison: sign + number, or sign + day (a range of days for "between") -->
              <div v-else class="qp-input" :class="{ 'is-open': openMenu === item.id }" data-menu-root>
                <button type="button" class="qp-sign" :aria-label="`${$t(item.label)}: ${$t('Comparison')}`"
                  :title="$t('Comparison')" aria-haspopup="listbox"
                  :aria-expanded="openMenu === item.id ? 'true' : 'false'" @click="toggleMenu(item.id, $event)">
                  {{ signLabel(item.currentVal) }}
                </button>
                <div v-if="openMenu === item.id" role="listbox" class="qp-menu left-0 top-[calc(100%+4px)] w-[64px]"
                  @keydown="onMenuKeydown">
                  <button v-for="val in item.list" :key="val" type="button" role="option"
                    :aria-selected="val === item.currentVal ? 'true' : 'false'"
                    :title="val === BETWEEN ? $t('Between') : null"
                    @click="handleSelectedValue(val, item.id)" class="qp-menu-item w-full justify-center"
                    :class="{ 'is-active': val === item.currentVal }">
                    {{ signLabel(val) }}
                  </button>
                </div>
                <template v-if="item.isDate">
                  <date-picker :lang="language" format="MM/DD/YYYY" v-model="item.value" input-class="qp-date-field"
                    :shortcuts="datePresets(item, 'value')" :placeholder="item.currentVal === BETWEEN ? $t('From') : 'MM/DD/YYYY'"
                    :input-attr="{ id: `filter-${item.key}`, 'aria-label': item.currentVal === BETWEEN ? `${$t(item.label)}: ${$t('From')}` : null }"></date-picker>
                  <template v-if="item.currentVal === BETWEEN">
                    <span class="self-center text-faint" aria-hidden="true">–</span>
                    <date-picker :lang="language" format="MM/DD/YYYY" v-model="item.valueMax"
                      input-class="qp-date-field" :shortcuts="datePresets(item, 'valueMax')" :placeholder="$t('To')"
                      :input-attr="{ id: `filter-${item.key}-max`, 'aria-label': `${$t(item.label)}: ${$t('To')}` }"></date-picker>
                  </template>
                </template>
                <input v-else :id="`filter-${item.key}`" v-model="item.value" @input="sanitizeNumberInput(item)"
                  @change="formatNumberInput(item)" type="text" inputmode="decimal" autocomplete="off"
                  class="qp-input-field" :aria-invalid="isInvalidNumber(item) ? 'true' : null"
                  :class="{ '!text-down': isInvalidNumber(item), '!px-1.5': item.currentVal === BETWEEN }"
                  :placeholder="item.currentVal === BETWEEN ? $t('Min') : null"
                  :aria-label="item.currentVal === BETWEEN ? `${$t(item.label)}: ${$t('Min')}` : null" />
                <template v-if="!item.isDate && item.currentVal === BETWEEN">
                  <span class="self-center text-faint" aria-hidden="true">–</span>
                  <input :id="`filter-${item.key}-max`" v-model="item.valueMax"
                    @input="sanitizeNumberInput(item, 'valueMax')" @change="formatNumberInput(item, 'valueMax')"
                    type="text" inputmode="decimal" autocomplete="off" class="qp-input-field !px-1.5"
                    :placeholder="$t('Max')" :aria-label="`${$t(item.label)}: ${$t('Max')}`"
                    :aria-invalid="isInvalidNumber(item, 'valueMax') ? 'true' : null"
                    :class="{ '!text-down': isInvalidNumber(item, 'valueMax') }" />
                </template>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Results toolbar -->
      <div class="shrink-0 flex flex-wrap items-center justify-between gap-3">
        <div class="qp-seg qp-seg-lg" role="tablist" :aria-label="$t('Column groups')">
          <button v-for="item in columnTabs" :key="item.name" type="button" role="tab"
            :aria-selected="item.name == selectedColumnName ? 'true' : 'false'"
            :class="{ 'is-active': item.name == selectedColumnName }" @click="handleSelectedColumn(item)">
            {{ $t(item.name) }}
          </button>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <span class="text-[13px] text-muted qp-num mr-1" aria-live="polite">
            <template v-if="checkedItems.length > 0">
              <b class="font-semibold text-ink">{{ checkedItems.length }}</b> {{ $t("record(s) selected") }}
            </template>
            <template v-else>
              <b class="font-semibold text-ink">{{ dataResult.length }}</b> {{ $t("stocks-found") }}
            </template>
          </span>
          <label class="flex items-center gap-1.5 text-[12px] text-muted">
            <span class="hidden lg:inline">{{ $t("Symbols per page") }}</span>
            <select :value="itemsPerPage" @change="onRowsChange" class="qp-select qp-select-sm"
              :aria-label="$t('Symbols per page')">
              <option v-for="option in rowOptions" :key="option" :value="option">
                {{ option === ALL_ROWS ? $t("All") : option }}
              </option>
            </select>
          </label>
          <button v-if="checkedItems.length" type="button" @click="handleOpenModal('symbol')"
            class="qp-btn qp-btn-ghost" :title="$t('Export selected symbols')">
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <rect x="1.75" y="1.75" width="12.5" height="12.5" rx="2.5" stroke="currentColor" stroke-width="1.4" />
              <path d="m5 8.2 2 2 4-4.4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
            <span class="hidden md:inline">{{ $t("Export selected symbols") }}</span>
          </button>
          <button type="button" @click="handleDownloadData" :disabled="!(dataResult.length > 0) || downloading"
            class="qp-btn qp-btn-ghost" :title="$t('Download')">
            <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke-width="1.7" stroke="currentColor"
              aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            <span class="hidden md:inline">{{ downloading ? $t("Downloading") : $t("Download") }}</span>
          </button>
          <button type="button" @click="handleOpenModal('result')" :disabled="!(dataResult.length > 0)"
            class="qp-btn qp-btn-ghost" :title="$t('Export results')">
            <svg width="15" height="15" viewBox="0 0 17 17" fill="none" aria-hidden="true">
              <path d="M9.2 7.8 15 2M15.6 4.8V1.4h-3.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"
                stroke-linejoin="round" />
              <path d="M7.8 1.4H6.4C2.8 1.4 1.4 2.8 1.4 6.4v4.2c0 3.6 1.4 5 5 5h4.2c3.6 0 5-1.4 5-5V9.2"
                stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span class="hidden md:inline">{{ $t("Export results") }}</span>
          </button>
          <div class="relative" data-menu-root>
            <button type="button" @click="toggleMenu('columns', $event)" class="qp-btn qp-btn-ghost"
              :title="$t('Columns visibility')" aria-haspopup="true"
              :aria-expanded="openMenu === 'columns' ? 'true' : 'false'">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <rect x="1.75" y="2.75" width="12.5" height="10.5" rx="1.5" stroke="currentColor" stroke-width="1.4" />
                <path d="M6 3v10M10 3v10" stroke="currentColor" stroke-width="1.4" />
              </svg>
              <span class="hidden md:inline">{{ $t("Columns visibility") }}</span>
            </button>
            <div v-if="openMenu === 'columns'"
              class="qp-menu w-[17rem] right-0 top-[calc(100%+6px)] max-h-[60vh] overflow-y-auto"
              @keydown="onMenuKeydown">
              <div v-for="(item, i) in orderedColumns" :key="item.key" class="flex items-center">
                <button type="button" role="checkbox" @click="changeColumn(item)"
                  :aria-checked="isColumnVisible(item) ? 'true' : 'false'" :disabled="item.key == 'symbol'"
                  class="qp-menu-item flex-1 min-w-0 text-left disabled:opacity-60 disabled:cursor-default">
                  <span class="qp-check" :class="{ 'is-checked': isColumnVisible(item) }">
                    <svg v-if="isColumnVisible(item)" width="10" height="10" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" stroke-width="4" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  </span>
                  <span class="truncate">{{ $t(item.value) }}</span>
                </button>
                <template v-if="item.key !== 'symbol'">
                  <button type="button" class="qp-menu-item !px-1.5" :disabled="i <= 1"
                    :class="{ 'opacity-30': i <= 1 }" :aria-label="`${$t('Move up')}: ${$t(item.value)}`"
                    :title="$t('Move up')" @click="moveColumn(item.key, -1)">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                      <path d="M2 6.5 5 3.5l3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  </button>
                  <button type="button" class="qp-menu-item !px-1.5" :disabled="i === orderedColumns.length - 1"
                    :class="{ 'opacity-30': i === orderedColumns.length - 1 }"
                    :aria-label="`${$t('Move down')}: ${$t(item.value)}`" :title="$t('Move down')"
                    @click="moveColumn(item.key, 1)">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                      <path d="M2 3.5 5 6.5l3-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  </button>
                </template>
              </div>
              <button v-if="columnPrefs[columnSetKey]" type="button"
                class="qp-menu-item w-full mt-1 border-t border-line-soft rounded-none text-[12px] text-muted"
                @click="resetColumns">
                {{ $t("Reset columns") }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="scanLoader" class="qp-card relative w-full flex-1 min-h-[300px] flex justify-center items-center">
        <Loading />
      </div>

      <div v-else-if="dataResult.length > 0"
        class="qp-card relative w-full flex-1 min-h-[440px] xl:min-h-0 flex flex-col overflow-hidden">
        <div ref="scroller" @scroll="handleScroll"
          class="!relative flex-1 min-h-[200px] bg-surface overflow-x-auto overflow-y-auto">
          <table class="w-full text-[13px] bg-surface text-left rtl:text-right">
            <thead class="z-50 sticky top-0 bg-surface-2 shadow-[inset_0_-1px_0_rgb(var(--qp-line-card))]">
              <tr>
                <th scope="col" class="py-2.5 px-[1rem] sticky left-0 z-30 bg-surface-2 w-6 text-center">
                  <div class="w-full h-full flex justify-center">
                    <button type="button" role="checkbox" @click="handleToggleAll"
                      :aria-checked="allChecked ? 'true' : someChecked ? 'mixed' : 'false'"
                      :aria-label="$t('Select all')" class="qp-check"
                      :class="{ 'is-checked': allChecked || someChecked }">
                      <svg v-if="allChecked" width="10" height="10" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="4" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                      <svg v-else-if="someChecked" width="10" height="10" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="4" aria-hidden="true">
                        <path stroke-linecap="round" d="M6 12h12" />
                      </svg>
                    </button>
                  </div>
                </th>

                <th v-for="col in visibleColumns" :key="col.key" scope="col" :aria-sort="ariaSort(col.key)"
                  :class="col.key == 'symbol' ? `sticky left-10 z-30 bg-surface-2 ${showShadow ? 'shadow-right' : ''}` : ''"
                  class="px-4 text-left py-2.5 whitespace-nowrap font-medium">
                  <div class="flex items-center gap-1.5">
                    <button type="button" @click="sortByColumn(col.key)" :title="$t(col.value)"
                      class="group inline-flex items-center gap-1 rounded text-ink-3 font-semibold text-[12px] hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40">
                      <span>{{ truncate($t(col.value)) }}</span>
                      <svg v-if="sortedColumn === col.key" width="12" height="12" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" class="text-brand" aria-hidden="true">
                        <path v-if="sortOrder === 'asc'" stroke-linecap="round" stroke-linejoin="round"
                          d="M4.5 10.5 12 3m0 0 7.5 7.5M12 3v18" />
                        <path v-else stroke-linecap="round" stroke-linejoin="round"
                          d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
                      </svg>
                      <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2" aria-hidden="true"
                        class="text-faint opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M7 9l5-5 5 5M7 15l5 5 5-5" />
                      </svg>
                    </button>
                    <span v-if="col.def" class="inline-flex cursor-help text-faint" tabindex="0"
                      :aria-label="$t(col.def)">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <circle cx="7" cy="7" r="5.8" stroke="currentColor" stroke-width="1.2" />
                        <path d="M7 6.3v3.4M7 4.4h.01" stroke="currentColor" stroke-width="1.4"
                          stroke-linecap="round" />
                      </svg>
                      <vue-easy-tooltip :backgroundColor="theme.isDark ? '#334155' : '#0F172A'">{{ $t(col.def) }}</vue-easy-tooltip>
                    </span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in paginatedProducts" :key="item.symbol" @click="toggleSingleCheckbox(item)"
                :class="isChecked(item) ? 'bg-up-bg/60' : 'hover:bg-surface-2'"
                class="border-b border-line-soft">
                <td class="py-1 px-1 border-b border-line-soft sticky left-0 z-30 w-6 text-center"
                  :class="showShadow ? 'bg-surface' : ''">
                  <div class="w-full h-full flex justify-center">
                    <button type="button" role="checkbox" :aria-checked="isChecked(item) ? 'true' : 'false'"
                      :aria-label="`${$t('Select')} ${item.symbol}`" class="qp-check"
                      :class="{ 'is-checked': isChecked(item) }" @click.stop="toggleSingleCheckbox(item)">
                      <svg v-if="isChecked(item)" width="10" height="10" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="4" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                    </button>
                  </div>
                </td>

                <td v-for="col in visibleColumns" :key="col.key"
                  :class="col.key == 'symbol' ? `sticky left-10 z-30 ${showShadow ? 'shadow-right bg-surface' : ''}` : ''"
                  class="px-4 py-1 text-[13px] border-b border-line-soft text-ink qp-num whitespace-nowrap">
                  <span v-if="col.key == 'symbol'" class="flex items-center gap-3">
                    <span
                      class="flex items-center justify-center rounded-lg bg-surface-3 ring-1 ring-line-soft w-7 h-7 overflow-hidden shrink-0 text-[13px] font-semibold text-ink-2">
                      <img v-if="item.logo && item.logo != 'N/A'" :src="item.logo" alt=""
                        class="w-7 h-7 object-contain bg-white rounded" />
                      <span v-else aria-hidden="true">{{ String(item.symbol || "?")[0] }}</span>
                    </span>
                    <span class="flex flex-col">
                      <a :href="quoteHref(item)" @click.stop="onTickerClick($event, item)"
                        class="font-semibold text-[14px] leading-4 text-ink hover:text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 rounded">
                        {{ item.symbol || "N/A" }}
                      </a>
                      <span class="text-[11px] leading-[14px] font-medium" :class="toneClass(item.chgPer)">
                        {{ formatPct(item.chgPer, true) }}
                      </span>
                    </span>
                  </span>

                  <span v-else-if="col.key == '52w_high' || col.key == '52w_low'" class="flex items-center gap-1">
                    <svg width="16" height="16" viewBox="0 0 20 21" fill="none" aria-hidden="true">
                      <path fill-rule="evenodd" clip-rule="evenodd"
                        d="M20 10.5C20 16.0228 15.5228 20.5 10 20.5C4.47715 20.5 0 16.0228 0 10.5C0 4.97715 4.47715 0.5 10 0.5C15.5228 0.5 20 4.97715 20 10.5ZM14.0303 7.46967C14.3232 7.76256 14.3232 8.23744 14.0303 8.5303L9.0303 13.5303C8.7374 13.8232 8.2626 13.8232 7.96967 13.5303L5.96967 11.5303C5.67678 11.2374 5.67678 10.7626 5.96967 10.4697C6.26256 10.1768 6.73744 10.1768 7.03033 10.4697L8.5 11.9393L10.7348 9.7045L12.9697 7.46967C13.2626 7.17678 13.7374 7.17678 14.0303 7.46967Z"
                        :class="isNewExtreme(item, col.key) ? 'fill-brand' : 'fill-line-strong'" />
                    </svg>
                    <span :class="isNewExtreme(item, col.key) ? 'text-up font-medium' : 'text-muted'">
                      {{ cellText(item, col.key) }}
                    </span>
                    <span v-if="isNewExtreme(item, col.key)" class="sr-only">({{ $t("new") }})</span>
                  </span>

                  <span v-else-if="col.key == 'ossBestContract' && contractParts(item.ossBestContract)"
                    class="inline-flex items-center gap-1.5 whitespace-nowrap" :title="item.ossBestContract">
                    <span :class="contractPill(contractParts(item.ossBestContract).type)">
                      {{ $t(contractParts(item.ossBestContract).type === "put" ? "Put" : "Call") }}
                    </span>
                    <span class="font-medium text-ink">{{ contractParts(item.ossBestContract).label }}</span>
                  </span>

                  <span v-else :class="cellClass(item, col.key)" :title="cellTitle(item, col.key)">{{ cellText(item, col.key) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          class="min-h-[3.5rem] px-4 py-2 flex-shrink-0 border-t border-line-soft flex flex-col sm:flex-row items-center justify-between gap-2">
          <p class="text-[13px] leading-5 text-muted qp-num">
            {{ $t('Showing') }}: {{ showingFrom }} {{ $t('to') }} {{ showingTo }} {{ $t('of') }}
            {{ sortedProducts.length }} {{ $t('stocks') }}
          </p>

          <nav :aria-label="$t('Pagination')">
            <ul class="pagination flex items-center border border-line rounded-lg h-8 bg-surface overflow-hidden qp-num">
              <li class="hidden sm:block">
                <button type="button" @click="changePage(1)" :disabled="currentPage === 1">{{ $t("First") }}</button>
              </li>
              <li>
                <button type="button" @click="changePage(currentPage - 1)" :disabled="currentPage === 1"
                  :aria-label="$t('Previous')">
                  <span class="hidden sm:inline">{{ $t("Previous") }}</span>
                  <span class="sm:hidden" aria-hidden="true">‹</span>
                </button>
              </li>
              <li v-for="(pageNumber, i) in paginationRange" :key="`${pageNumber}-${i}`">
                <button v-if="pageNumber !== '...'" type="button" @click="changePage(pageNumber)"
                  :aria-current="pageNumber === currentPage ? 'page' : null"
                  :class="{ 'is-current': pageNumber === currentPage }">
                  {{ pageNumber }}
                </button>
                <span v-else class="text-faint" aria-hidden="true">…</span>
              </li>
              <li>
                <button type="button" @click="changePage(currentPage + 1)" :disabled="currentPage >= totalPages"
                  :aria-label="$t('Next')">
                  <span class="hidden sm:inline">{{ $t("Next") }}</span>
                  <span class="sm:hidden" aria-hidden="true">›</span>
                </button>
              </li>
              <li class="hidden sm:block">
                <button type="button" @click="changePage(totalPages)" :disabled="currentPage >= totalPages">
                  {{ $t("Last") }}
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <div v-else class="qp-card w-full h-[200px] flex items-center justify-center text-[14px] text-muted">
        {{ $t('no-data') }}
      </div>
    </div>

    <!-- AI assistant: a column next to the results on wide screens, a sheet over them below xl. -->
    <ScanAssistant v-show="assistantOpen" ref="assistant"
      class="fixed inset-y-0 right-0 z-[60] w-full sm:w-[400px] shadow-2xl xl:static xl:z-auto xl:w-[380px] xl:shrink-0 xl:shadow-none xl:border-l xl:border-line-card"
      :current-filters="assistantCurrentFilters" :apply-filters="applyAssistantFilters"
      :restore-query="restoreAssistantQuery" :describe-filter="describeAssistantFilter"
      :current-view="assistantCurrentView" :describe-view="describeAssistantView"
      :current-results="assistantCurrentResults"
      @close="setAssistantOpen(false)" />
    </div>

    <button v-if="!assistantOpen" type="button" @click="setAssistantOpen(true)"
      class="fixed right-0 top-1/2 -translate-y-1/2 z-[60] flex flex-col items-center gap-2 py-3 px-1.5 rounded-l-xl bg-inverse text-inverse-ink shadow-lg hover:bg-inverse-hover transition"
      :title="$t('assistant-open-hint')" :aria-label="$t('AI assistant')">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
        stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M12 3l1.8 4.6L18.5 9.5l-4.7 1.9L12 16l-1.8-4.6L5.5 9.5l4.7-1.9z" />
        <path d="M19 15l.7 1.8 1.8.7-1.8.7L19 20l-.7-1.8-1.8-.7 1.8-.7z" />
      </svg>
      <span class="text-[12px] font-semibold tracking-wide [writing-mode:vertical-rl] rotate-180">{{ $t("AI assistant") }}</span>
    </button>

    <!-- Export format -->
    <BaseModal v-if="showExportModal" :label="$t('Choose a file format')" @close="handleCloseModal">
      <div class="w-[min(35rem,calc(100vw-3rem))] p-4">
        <div class="w-full flex items-center mb-6 justify-between">
          <h2 class="text-[18px] font-semibold text-ink">
            {{ $t("Choose a file format") }}
            <span class="block text-[13px] font-normal text-muted">
              {{ selectedExportType === 'symbol' ? $t("Export selected symbols") : $t("Export results") }}
            </span>
          </h2>
          <button type="button" @click="handleCloseModal" :aria-label="$t('Close')"
            class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-surface-3">
            <svg width="14" height="14" viewBox="0 0 16 17" fill="none" class="text-muted" aria-hidden="true">
              <line x1="14.7071" y1="1.70711" x2="0.707107" y2="15.7071" stroke="currentColor" stroke-width="2" />
              <line x1="0.707107" y1="1.29289" x2="14.7071" y2="15.2929" stroke="currentColor" stroke-width="2" />
            </svg>
          </button>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 my-4" role="radiogroup" :aria-label="$t('Choose a file format')">
          <button v-for="item in modalExportList" :key="item.key" type="button" role="radio"
            :aria-checked="selectedExport.key == item.key ? 'true' : 'false'" @click="selectedExport = item"
            class="flex items-center gap-3 p-3 rounded-xl border text-left transition"
            :class="selectedExport.key == item.key ? 'border-brand bg-up-bg/60' : 'border-line hover:bg-surface-2'">
            <img :src="require(`../../assets/${item.svg}`)" alt="" class="h-8 w-8 object-contain" />
            <span class="text-[14px] font-medium text-ink">{{ $t(item.val) }}</span>
          </button>
        </div>
        <div class="w-full flex items-center mt-8 justify-center">
          <button type="button" class="qp-btn qp-btn-primary w-48" @click="handleDownload">{{ $t("Download") }}</button>
        </div>
      </div>
    </BaseModal>

    <!-- Feedback -->
    <BaseModal v-if="feedbackModal" :label="$t('Feedback & Suggestions')" @close="handleClose">
      <div class="flex flex-col p-6 gap-5 w-[min(30rem,calc(100vw-3rem))]"
        :class="feedbackMsg ? '' : 'max-h-[85vh] overflow-y-auto'">
        <div class="w-full flex items-center justify-between">
          <h2 class="text-[16px] font-semibold text-ink">{{ $t('Feedback & Suggestions') }}</h2>
          <button type="button" @click="handleClose" :aria-label="$t('Close')"
            class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-surface-3">
            <svg width="12" height="12" viewBox="0 0 19 19" fill="none" class="text-faint" aria-hidden="true">
              <path d="M1.5 1.5l16 16M17.5 1.5l-16 16" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
            </svg>
          </button>
        </div>

        <template v-if="!feedbackMsg">
          <label class="w-full flex flex-col gap-1">
            <span class="qp-field-label">{{ $t('Subject') }}<span class="text-down"> *</span></span>
            <span class="qp-input"><input class="qp-input-field" v-model="formData.subject" /></span>
          </label>

          <label class="w-full flex flex-col gap-1">
            <span class="qp-field-label">{{ $t("Message Type") }}<span class="text-down"> *</span></span>
            <select class="qp-select w-full" v-model="formData.messageType">
              <option disabled value="0">{{ $t('Select a type') }}</option>
              <option v-for="option in options.slice(1)" :key="option.id" :value="option.id">
                {{ $t(option.label) }}
              </option>
            </select>
          </label>

          <label class="w-full flex flex-col gap-1">
            <span class="qp-field-label">{{ $t('Your message') }}<span class="text-down"> *</span></span>
            <textarea v-model="formData.message" :maxlength="500"
              class="h-28 py-2 px-3 w-full border border-line rounded-lg bg-surface text-ink text-[13px] resize-none outline-none focus:border-brand focus:ring-[3px] focus:ring-brand/15"></textarea>
            <span class="text-[11px]" :class="formData.message.length < 500 ? 'text-faint' : 'text-down'">
              {{ $t("Upto 500 characters") }}
            </span>
          </label>

          <div class="w-full flex flex-col gap-1">
            <span class="qp-field-label">{{ $t('Attachment(s) - up to 5 files') }}</span>
            <label for="csvUpload" @dragover.prevent @drop="handleDrop" tabindex="0"
              @keydown.enter.prevent="$refs.fileInput.click()" @keydown.space.prevent="$refs.fileInput.click()"
              class="gap-2 flex justify-center items-center h-14 py-2 px-4 w-full border-2 border-dashed border-line bg-surface hover:bg-surface-2 rounded-lg text-[12px] cursor-pointer">
              <svg width="18" height="18" viewBox="0 0 21 21" fill="none" class="text-ink-2" aria-hidden="true">
                <path
                  d="M14.667 8.00146C16.4795 8.01156 17.4611 8.09193 18.1014 8.73226C18.8337 9.46451 18.8337 10.643 18.8337 13V13.8333C18.8337 16.1903 18.8337 17.3689 18.1014 18.1011C17.3692 18.8333 16.1907 18.8333 13.8337 18.8333H7.16699C4.80997 18.8333 3.63146 18.8333 2.89923 18.1011C2.16699 17.3689 2.16699 16.1903 2.16699 13.8333V13C2.16699 10.643 2.16699 9.46451 2.89923 8.73225C3.53954 8.09193 4.52113 8.01156 6.33366 8.00146"
                  stroke="currentColor" stroke-linecap="round" />
                <path d="M10.5 12.9998V2.1665M10.5 2.1665L13 5.08317M10.5 2.1665L8 5.08317" stroke="currentColor"
                  stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span class="flex flex-col">
                <span>{{ $t("Click to upload or drag and drop") }}</span>
                <span class="text-faint text-[11px]">{{ $t('Max file size 50 MB') }}</span>
              </span>
              <input ref="fileInput" type="file" id="csvUpload" @change="handleFileUpload" class="hidden" multiple />
            </label>
          </div>

          <div v-if="csvFiles.length" class="flex flex-wrap gap-2">
            <span v-for="(item, index) in csvFiles" :key="index"
              class="flex items-center gap-2 py-1 px-2 border border-line rounded-md text-[11px]">
              <span class="truncate max-w-[8rem]">{{ item.name }}</span>
              <span class="text-faint">{{ (item.size / 1024).toFixed(1) }} KB</span>
              <button type="button" @click="removeFile(index)" :aria-label="`${$t('Delete')} ${item.name}`"
                class="text-faint hover:text-down">×</button>
            </span>
          </div>

          <div class="flex flex-col items-end gap-2">
            <span v-if="isError" role="alert" class="text-[12px] text-down">
              {{ submitError || $t('All fields with * are required') }}
            </span>
            <button type="button" @click="handleSubmit" :disabled="isLoader" class="qp-btn qp-btn-primary w-32">
              {{ isLoader ? `${$t('Submitting')}...` : $t('Submit') }}
            </button>
          </div>
        </template>

        <div v-else class="flex flex-col w-full gap-5" role="status">
          <div class="flex w-full items-center gap-3 mt-2">
            <svg viewBox="0 0 24 24" class="w-10 h-10 fill-brand shrink-0" aria-hidden="true">
              <path fill-rule="evenodd"
                d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
                clip-rule="evenodd" />
            </svg>
            <span class="text-[13px]">{{ $t("successMsg") }}</span>
          </div>
          <button type="button" @click="handleClose" class="qp-btn qp-btn-dark self-end">{{ $t("Close") }}</button>
        </div>
      </div>
    </BaseModal>

    <QuoteBaseModal v-if="showQuoteModal" @close="closeQuote">
      <div class="!w-full">
        <Quote @handleClose="closeQuote" @symbolChange="onQuoteSymbolChange" :Qoutesymbol="selectedSymbol" />
      </div>
    </QuoteBaseModal>
  </div>
</template>

<script>
import BaseModal from "../Modal/BaseModal.vue";
import VueEasyTooltip from "vue-easy-tooltip";
import DatePicker from "vue2-datepicker";
import axios from "../../api/axios.js";
import "vue2-datepicker/index.css";
import moment from "moment";
import "vue2-datepicker/locale/fr";
import {
  createFilters, scanDefinition, columnGroups, scanNameMap, BETWEEN,
  RESULT_CAP, UNIVERSE_SCAN, UNIVERSE_DEFAULT_FILTERS, TOP_DOWN_SCAN, setIndustryOptions,
  STRADDLE_SCAN, PUT_CREDIT_SCAN, CALL_DEBIT_SCAN, COMBO_SCANS,
} from "../../utils/data";
import { parseUserNumber, formatUserNumber, date as formatDate } from "../../utils/format";
import { parseOccSymbol } from "../../utils/occ";
import { STRANGLE_IV_HV_MAX, nullReasonKey } from "../../utils/strangleVol";
import Loading from "../loading/Loading.vue";
import Quote from "@/pages/Quote.vue";
import QuoteBaseModal from "../Modal/QuoteBaseModal.vue";
import ScanAssistant from "../assistant/ScanAssistant.vue";
import { filtersToQuery, filtersFromPayload } from "../../utils/assistant";
import { readStorage, writeStorage } from "../../utils/storage";
import ThemeToggle from "../ThemeToggle.vue";
import { theme } from "../../theme";

// "All" in the rows-per-page select.
const ALL_ROWS = 0;
const ROW_OPTIONS = [10, 25, 50, 100, ALL_ROWS];
const DEFAULT_ROWS = 25;
const DEFAULT_SCAN = UNIVERSE_SCAN;
// Links and saved scans made before All Stocks became the default left `scan` out for this one.
const LEGACY_DEFAULT_SCAN = "Candlestick Scan";

// Per-browser preferences (never needed for correctness: every read falls back).
const PRESETS_KEY = "oppscan.savedScans";
const COLUMNS_KEY = "oppscan.columns";
const ASSISTANT_KEY = "oppscan.assistantOpen";
// Tailwind's xl: from here the assistant is a column next to the results instead of a sheet over them.
const WIDE_SCREEN = 1280;

// Columns whose values are dates ("MM/DD/YYYY" or ISO) and must sort as dates.
const DATE_COLUMNS = ["nextEarningDate", "ossBestExpiration", "ossChainDate", "dividendExDate", "dividendPayDate", "cbExpiration", "cbExit"];
// Option combo scans: default sort column and order of each (the backend's Combo.Rank order).
const COMBO_SCAN_SORT = {
  [STRADDLE_SCAN]: ["cbSlippage", "asc"],
  [PUT_CREDIT_SCAN]: ["cbCreditPct", "desc"],
  [CALL_DEBIT_SCAN]: ["cbRs", "desc"],
};
// Report time of the next earnings (Finnhub): the acronym after the date, the full wording on hover.
const EARNING_SESSIONS = { bmo: "Before open", amc: "After close", dmh: "During market hours" };
const COMPACT_COLUMNS = ["volume", "avgVolume10day", "avgVolume3month"];
const SCORE_COLUMNS = ["oss", "ossIvCheapness", "ossEarningsCycle", "ossDteOpportunity", "ossTermStructure", "ossLiquidity", "ossSkew"];
const PERCENT_COLUMNS = ["roa", "roe", "roi", "chgPer", "perfYtd", "ossCheapVsHistory", "ivPct", "gap_percent", "gap_vol_percent", "gapHaussMoyenPercent", "gapBaissMoyenPercent", "mouvHaussPercent", "mouvBaissPercent", "tdFromHigh", "tdAboveLow", "tdDistEma21", "cbSlippage", "cbImpliedMove", "cbCreditPct", "cbPop", "cbIvVsPreReport"];
const PRICE_COLUMNS = ["close", "52w_high", "52w_low", "sma20", "sma50", "sma200", "gapHaussMoyen", "gapBaissMoyen", "mouvHauss", "mouvBaiss", "dividend", "dividendAmount", "cbMid", "cbNatural", "cbMaxLoss", "cbMaxGain", "cbBreakeven", "cbTheta"];
const INTEGER_COLUMNS = ["jourEarnings", "ossBestDte", "tdRs", "cbDte", "cbSessions", "cbRs", "cbMinOi"];
const SIGNED_COLUMNS = ["chgPer", "perfYtd", "ossCheapVsHistory", "tdFromHigh", "tdAboveLow", "tdDistEma21"];
// Top-Down scan: entry state labels (the stored code -> label key) and fundamentals grade colours.
const ENTRY_LABELS = { at_ema21: "At EMA21", deep_pullback: "Deep pullback", extended: "Extended" };
const GRADE_CLASSES = { A: "text-up font-semibold", B: "text-up", C: "text-amber", D: "text-down", F: "text-down font-semibold" };
// Columns where 0 only means "no data" (mapResults turns a missing value into 0): sorted with the missing values, so
// "lowest RSI" or "smallest market cap" do not start with the rows that have none.
const ZERO_IS_MISSING = ["mktcap", "close", "rsi", "atr", "sma20", "sma50", "sma200", "52w_high", "52w_low", "avgVolume10day", "avgVolume3month", "ivRank", "ivRankPlus"];

// Row fields the AI assistant reads to answer questions about the results on screen (the backend's
// ScanResultRows.Fields: it drops any other), and how many rows go with a request: the first rows of the page, then
// the results the request names by ticker, wherever they are (each row also gets its position, "n").
const ASSISTANT_RESULT_FIELDS = [
  "symbol", "companyName", "sector", "industry", "exchange", "securityType", "mktcap", "marketCapCurrency", "close",
  "chgPer", "volume", "relVolume", "rsi", "sma50", "sma200", "ivPct", "ivRank", "ivRankPlus", "nextEarningDate",
  "earningHour", "jourEarnings", "typeChandelle", "perfYtd", "gap_percent", "dividend", "dividendExDate", "dividendAmount",
  "dividendPayDate", "tdGroup", "tdRs", "tdEntry", "tdDistEma21", "tdFromHigh", "tdAboveLow", "tdGrade",
];
const ASSISTANT_MAX_RESULTS = 30;
const ASSISTANT_PAGE_RESULTS = 25;
// The day's move: mapResults gives 0 (or NaN) for these without the latest candle and the one before it.
const MOVE_COLUMNS = ["chgPer", "volume", "gap_percent"];

function isMissing(v) {
  return v === null || v === undefined || v === "" || v === "N/A" || (typeof v === "number" && !isFinite(v));
}

function sortValue(v, key) {
  if (isMissing(v)) return null;
  if (ZERO_IS_MISSING.includes(key) && Number(v) === 0) return null;
  if (DATE_COLUMNS.includes(key)) {
    const d = moment(v, ["MM/DD/YYYY", "YYYY-MM-DD", moment.ISO_8601], true);
    return d.isValid() ? d.valueOf() : null;
  }
  if (typeof v === "number") return v;
  const n = Number(v);
  if (String(v).trim() !== "" && !isNaN(n)) return n;
  return String(v).toLowerCase();
}

export default {
  name: "ScanTableComponent",
  components: {
    BaseModal,
    VueEasyTooltip,
    DatePicker,
    Loading,
    Quote,
    QuoteBaseModal,
    ScanAssistant,
    ThemeToggle,
  },
  props: {
    dataResult: {
      type: Array,
      default: () => [],
    },
    scanLoader: {
      type: Boolean,
      default: false,
    },
    latestDateWithAPI: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      ALL_ROWS,
      theme,
      BETWEEN,
      RESULT_CAP,
      showQuoteModal: false,
      // True when the details were opened from this screener (so closing can go
      // back in history instead of replacing the URL).
      quoteOpenedFromScreener: false,
      // True when the scan date was restored from the URL: the latest-date
      // watcher must not override it.
      dateFromQuery: false,
      // The URL is only rewritten once the first scan has run, so restoring
      // state from the query cannot clobber the query it was read from.
      queryReady: false,
      selectedSymbol: null,

      // Filters
      scannerList: createFilters(),
      selectedScanner: "Descriptive",
      showFilter: true,
      openMenu: null,
      // Payload of the last scan, to tell when the filters were edited since.
      lastRunSignature: null,

      // Scan type and date
      // The scan dropdown: stock scans, then the option ones.
      scanGroups: [
        { label: "Stock scans", scans: [UNIVERSE_SCAN, "Candlestick Scan", "Combo Scan", "Gaps Scan", TOP_DOWN_SCAN] },
        { label: "Option scans", scans: ["Sweet Spot Scan", ...COMBO_SCANS] },
      ],
      selectedSelector: DEFAULT_SCAN,
      selectedDate: null,

      // Table
      columns: [{ name: "Scan", colItems: scanDefinition[DEFAULT_SCAN] || [] }, ...columnGroups],
      selectedColumnName: "Scan",
      columnPrefs: readStorage(COLUMNS_KEY, {}),
      sortedColumn: "",
      sortOrder: "asc",
      checkedItems: [],
      currentPage: 1,
      itemsPerPage: DEFAULT_ROWS,
      rowOptions: ROW_OPTIONS,
      showShadow: false,
      screenWidth: window.innerWidth,

      // AI assistant panel
      // Reopened on load only where it does not cover the results.
      assistantOpen: window.innerWidth >= WIDE_SCREEN && readStorage(ASSISTANT_KEY, false) === true,

      // Saved scans
      savedScans: readStorage(PRESETS_KEY, []),
      presetName: "",

      // Export / download
      downloading: false,
      showExportModal: false,
      selectedExportType: null,
      modalExportList: [
        { key: "traderWork", val: "Trader Workstation", svg: "workstation.svg" },
        { key: "csv", val: "CSV", svg: "csv.svg" },
        { key: "generic", val: "Plain File", svg: "plainText.svg" },
      ],
      selectedExport: { key: "csv", val: "CSV" },

      // Feedback
      feedbackModal: false,
      feedbackMsg: false,
      isLoader: false,
      isError: false,
      submitError: "",
      formData: { subject: "", messageType: "0", message: "" },
      csvFiles: [],
      options: [
        { id: 0, label: "Select a type" },
        { id: "Bug", label: "Bug" },
        { id: "FeatureRequest", label: "Feature Request" },
        { id: "Suggestion", label: "Suggestion" },
      ],

      language: this.$i18n.locale,
    };
  },
  computed: {
    // ---- Filters ---------------------------------------------------------------
    currentSelects() {
      const tab = this.scannerList.find((t) => t.name === this.selectedScanner);
      return tab ? tab.selects : [];
    },
    // Active filters per category, shown as badges on the filter tabs.
    filterCounts() {
      const counts = {};
      this.scannerList.forEach((category) => {
        counts[category.name] = category.selects.filter((select) => this.isFilterActive(select)).length;
      });
      return counts;
    },
    activeFilterCount() {
      return Object.values(this.filterCounts).reduce((sum, n) => sum + n, 0);
    },
    currentSignature() {
      return JSON.stringify(this.buildPayload());
    },
    filtersDirty() {
      return this.lastRunSignature !== null && this.currentSignature !== this.lastRunSignature;
    },
    latestDate() {
      return this.latestDateWithAPI ? moment(this.latestDateWithAPI, "YYYY-MM-DD").toDate() : null;
    },
    scanDateShortcuts() {
      return [{ text: this.$t("Latest"), onClick: () => this.latestDate || new Date() }];
    },
    isUniverse() {
      return this.selectedSelector === UNIVERSE_SCAN;
    },
    resultCapLabel() {
      return formatUserNumber(RESULT_CAP, this.language);
    },

    // ---- Columns ---------------------------------------------------------------
    // The "Scan" column set belongs to a scan type; the universe is not one.
    columnTabs() {
      return this.isUniverse ? this.columns.filter((c) => c.name !== "Scan") : this.columns;
    },
    selectedColumn() {
      return this.columns.find((c) => c.name === this.selectedColumnName) || this.columns[0];
    },
    // Preferences are kept per column set: each scan type, and each group.
    columnSetKey() {
      return this.selectedColumnName === "Scan" ? `scan:${this.selectedSelector}` : this.selectedColumnName;
    },
    orderedColumns() {
      const items = this.selectedColumn.colItems;
      const order = (this.columnPrefs[this.columnSetKey] || {}).order || [];
      const rank = (key) => {
        if (key === "symbol") return -1;
        const i = order.indexOf(key);
        return i === -1 ? order.length + items.findIndex((c) => c.key === key) : i;
      };
      return [...items].sort((a, b) => rank(a.key) - rank(b.key));
    },
    visibleColumns() {
      return this.orderedColumns.filter((c) => this.isColumnVisible(c));
    },

    // ---- Rows ------------------------------------------------------------------
    sortedProducts() {
      const rows = [...this.dataResult];
      const key = this.sortedColumn;
      if (!key) return rows;
      const dir = this.sortOrder === "desc" ? -1 : 1;
      const values = new Map(rows.map((row) => [row, sortValue(row[key], key)]));
      return rows.sort((a, b) => {
        const va = values.get(a);
        const vb = values.get(b);
        // Missing values always go last, whatever the direction.
        if (va === null && vb === null) return 0;
        if (va === null) return 1;
        if (vb === null) return -1;
        if (typeof va !== typeof vb) return String(va).localeCompare(String(vb)) * dir;
        if (va < vb) return -dir;
        if (va > vb) return dir;
        return 0;
      });
    },
    pageSize() {
      return this.itemsPerPage === ALL_ROWS ? Math.max(this.sortedProducts.length, 1) : this.itemsPerPage;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.sortedProducts.length / this.pageSize));
    },
    startIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },
    showingFrom() {
      return this.sortedProducts.length ? this.startIndex + 1 : 0;
    },
    showingTo() {
      return Math.min(this.startIndex + this.pageSize, this.sortedProducts.length);
    },
    paginatedProducts() {
      return this.sortedProducts.slice(this.startIndex, this.startIndex + this.pageSize);
    },
    paginationRange() {
      const total = this.totalPages;
      const delta = this.screenWidth <= 430 ? 1 : 2;
      const range = [];
      for (let i = Math.max(2, this.currentPage - delta); i <= Math.min(total - 1, this.currentPage + delta); i++) {
        range.push(i);
      }
      if (this.currentPage - delta > 2) range.unshift("...");
      if (this.currentPage + delta < total - 1) range.push("...");
      range.unshift(1);
      if (total > 1) range.push(total);
      return range;
    },
    checkedSymbols() {
      return new Set(this.checkedItems.map((item) => item.symbol));
    },
    allChecked() {
      return this.dataResult.length > 0 && this.checkedItems.length === this.dataResult.length;
    },
    someChecked() {
      return this.checkedItems.length > 0 && !this.allChecked;
    },
  },
  methods: {
    // ---- Filters ---------------------------------------------------------------
    // Hover text of a filter: its label, and its definition when it has one (IV Rank+...).
    filterTitle(select) {
      return select.def ? `${this.$t(select.label)}: ${this.$t(select.def)}` : this.$t(select.label);
    },
    isFilterActive(select) {
      if (select.isCheckbox) return select.value === true;
      if (select.isDropdown) return !!select.value && select.value !== "all";
      if (select.isText) return String(select.value ?? "").trim() !== "";
      const [min, max] = select.isDate ? this.dayBounds(select) : this.numberBounds(select);
      return min !== null || max !== null;
    },
    // [first, last] day ("YYYY-MM-DD") of a date filter; last is only set for "between". Either may be null.
    dayBounds(select) {
      const day = (d) => (d && moment(d).isValid() ? moment(d).format("YYYY-MM-DD") : null);
      return [day(select.value), select.currentVal === BETWEEN ? day(select.valueMax) : null];
    },
    // Calendar shortcuts of a date filter ("Next 7 days"): turn it into that range of days from today. The picker
    // they are clicked in sets its own `field` from the returned day; the other bound is set here.
    datePresets(select, field) {
      const day = (offset) => moment().startOf("day").add(offset, "days").toDate();
      return (select.presets || []).map((p) => ({
        text: this.$t(p.label),
        onClick: () => {
          select.currentVal = BETWEEN;
          if (field === "value") select.valueMax = day(p.to);
          else select.value = day(p.from);
          return day(field === "value" ? p.from : p.to);
        },
      }));
    },
    // The backend's { sign, value, valueMax } from a filter's bounds; a range with one empty side is a plain >= / <=.
    comparison(sign, [min, max]) {
      if (sign !== BETWEEN) return { sign, value: min };
      if (max === null) return { sign: ">=", value: min };
      if (min === null) return { sign: "<=", value: max };
      return { sign: BETWEEN, value: min, valueMax: max };
    },
    // [lower, upper] of a number filter; upper is only set for "between". Either may be null (empty or invalid).
    numberBounds(select) {
      const locale = this.$i18n.locale;
      const min = parseUserNumber(select.value, locale);
      return [min, select.currentVal === BETWEEN ? parseUserNumber(select.valueMax, locale) : null];
    },
    isInvalidNumber(select, field = "value") {
      const raw = select[field];
      return String(raw ?? "").trim() !== "" && parseUserNumber(raw, this.$i18n.locale) === null;
    },
    signLabel(sign) {
      return sign === BETWEEN ? "↔" : sign;
    },
    isOptionSelected(select, option) {
      return option.key === select.value || (!select.value && option.key === "all");
    },
    // Keep only characters that can be part of a number while typing (K/M/B/T/Md suffixes included).
    sanitizeNumberInput(select, field = "value") {
      const clean = String(select[field] ?? "").replace(/[^0-9.,\s\-kmbtdKMBTD]/g, "");
      if (clean !== select[field]) select[field] = clean;
    },
    // On blur / Enter: show the number back in the UI language (1,500.5 / 1 500,5).
    formatNumberInput(select, field = "value") {
      const n = parseUserNumber(select[field], this.$i18n.locale);
      if (n !== null) select[field] = formatUserNumber(n, this.$i18n.locale);
    },
    // Enter in a filter field runs the scan (on buttons it keeps its usual meaning).
    onFilterEnter(e) {
      if (!e.target.matches("input") || e.target.closest(".qp-menu")) return;
      const select = this.currentSelects.find((s) => [`filter-${s.key}`, `filter-${s.key}-max`].includes(e.target.id));
      if (select && !select.isText && !select.isDate) {
        this.formatNumberInput(select);
        if (select.currentVal === BETWEEN) this.formatNumberInput(select, "valueMax");
      }
      this.openMenu = null;
      this.runScan();
    },
    handleSelectedValue(val, id) {
      const select = this.currentSelects.find((s) => s.id === id);
      if (select) {
        if (select.isDropdown) {
          select.currentVal = val.value;
          select.value = val.key === "all" ? "" : val.key;
        } else {
          select.currentVal = val;
        }
      }
      this.closeMenu();
    },
    handleSelectedScanner(item) {
      this.openMenu = null;
      this.selectedScanner = item.name;
    },
    // Back to the starting filters of the current view (the universe has defaults).
    handleResetFilter() {
      this.scannerList = createFilters();
      if (this.isUniverse) this.applyUniverseDefaults();
    },
    // Sets the universe's default filters on the ones the user has left empty.
    applyUniverseDefaults() {
      this.scannerList.forEach((category) => {
        category.selects.forEach((select) => {
          const key = UNIVERSE_DEFAULT_FILTERS[select.key];
          if (!key || this.isFilterActive(select)) return;
          const option = select.list.find((o) => o.key === key);
          if (option) {
            select.value = option.key;
            select.currentVal = option.value;
          }
        });
      });
    },

    // ---- Menus (dropdowns, saved scans, columns) --------------------------------
    toggleMenu(name, event) {
      if (this.openMenu === name) {
        this.openMenu = null;
        return;
      }
      this.openMenu = name;
      this.menuOpener = event && event.currentTarget;
      // Focus the selected option (or the first control) so arrow keys work
      // right away and long lists open scrolled to the current choice.
      this.$nextTick(() => {
        const root = this.menuOpener && this.menuOpener.closest("[data-menu-root]");
        const menu = root && root.querySelector(".qp-menu");
        if (!menu) return;
        const target = menu.querySelector(".is-active") || menu.querySelector("button:not([disabled]), input");
        if (target) target.focus({ preventScroll: false });
      });
    },
    closeMenu(restoreFocus = true) {
      this.openMenu = null;
      if (restoreFocus && this.menuOpener && document.contains(this.menuOpener)) this.menuOpener.focus();
      this.menuOpener = null;
    },
    // Arrow keys move between the menu's buttons; Esc closes it.
    onMenuKeydown(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        this.closeMenu();
        return;
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const items = [...e.currentTarget.querySelectorAll("button:not([disabled])")];
      if (!items.length) return;
      e.preventDefault();
      const i = items.indexOf(document.activeElement);
      const next = e.key === "ArrowDown" ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
      items[next].focus();
    },
    onDocumentPointer(e) {
      if (this.openMenu && !e.target.closest("[data-menu-root]")) this.closeMenu(false);
    },
    onDocumentKeydown(e) {
      if (this.showQuoteModal || this.showExportModal || this.feedbackModal) return;
      if (e.key === "Escape" && this.openMenu) {
        this.closeMenu();
        return;
      }
      // "/" jumps to the symbol filter, like search boxes elsewhere.
      const typing = e.target.closest("input, textarea, select, [contenteditable='true']");
      if (e.key === "/" && !typing && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        this.showFilter = true;
        this.selectedScanner = "Descriptive";
        this.$nextTick(() => {
          const input = this.$el.querySelector("#filter-symbol");
          if (input) input.focus();
        });
      }
    },

    // ---- Scan -------------------------------------------------------------------
    // The request body for the current filters, scan type and date. Empty filters
    // are left out; a typed 0 is a real filter.
    buildPayload() {
      const payload = {};
      const locale = this.$i18n.locale;
      this.scannerList.forEach((category) => {
        category.selects.forEach((select) => {
          if (!this.isFilterActive(select)) return;
          if (select.isDropdown) {
            payload[select.key] = select.value;
          } else if (select.isCheckbox) {
            payload[select.key] = true;
          } else if (select.isText) {
            payload[select.key] = select.value.trim().toUpperCase();
          } else if (select.isDate) {
            payload[select.key] = this.comparison(select.currentVal, this.dayBounds(select));
          } else if (select.currentVal !== BETWEEN) {
            payload[select.key] = { sign: select.currentVal, value: parseUserNumber(select.value, locale) };
          } else {
            payload[select.key] = this.comparison(BETWEEN, this.numberBounds(select));
          }
        });
      });
      payload.scanName = scanNameMap[this.selectedSelector];
      payload.date = this.getDate();
      return payload;
    },
    runScan({ keepPage = false } = {}) {
      const payload = this.buildPayload();
      this.lastRunSignature = JSON.stringify(payload);
      this.checkedItems = [];
      if (!keepPage) this.currentPage = 1;
      this.$emit("runScan", payload);
      this.queryReady = true;
      this.syncQuery();
    },
    getDate() {
      // The universe is the latest snapshot whatever day the picker was left on.
      if (this.isUniverse && this.latestDateWithAPI) return this.latestDateWithAPI;
      const date = this.selectedDate || this.latestDate;
      return date ? moment(date).format("YYYY-MM-DD") : this.latestDateWithAPI;
    },
    // Scans only exist for trading days up to the latest built day.
    isDisabledScanDate(date) {
      const day = date.getDay();
      if (day === 0 || day === 6) return true;
      return !!this.latestDate && date > this.latestDate;
    },
    onScanDateChange(date) {
      if (!date) return;
      const d = new Date(date);
      d.setHours(20, 0, 0, 0);
      this.selectedDate = d;
    },
    handleScanSelector(item) {
      this.applyScanSelector(item);
      // Only when picked here: a URL or saved scan keeps exactly the filters it describes.
      if (item === UNIVERSE_SCAN) this.applyUniverseDefaults();
      this.runScan();
    },
    applyScanSelector(item) {
      const wasUniverse = this.isUniverse;
      this.selectedSelector = item;
      this.columns = this.columns.map((c) => (c.name === "Scan" ? { ...c, colItems: scanDefinition[item] || [] } : c));
      if (item === UNIVERSE_SCAN) {
        if (this.selectedColumnName === "Scan") this.selectedColumnName = "Overview";
      } else if (wasUniverse) {
        this.selectedColumnName = "Scan";
      }
      if (item === UNIVERSE_SCAN) {
        // Largest companies first, the order the backend returns them in.
        this.sortedColumn = "mktcap";
        this.sortOrder = "desc";
      } else if (item === "Sweet Spot Scan") {
        // The sweet spot list is a ranking: always open it best-first.
        this.sortedColumn = "oss";
        this.sortOrder = "desc";
      } else if (item === TOP_DOWN_SCAN) {
        // Strongest relative strength first.
        this.sortedColumn = "tdRs";
        this.sortOrder = "desc";
      } else if (COMBO_SCAN_SORT[item]) {
        // The backend's order of the day: cheapest straddles to trade, richest credit, strongest RS.
        [this.sortedColumn, this.sortOrder] = COMBO_SCAN_SORT[item];
      } else if (this.sortedColumn && !this.isKnownColumn(this.sortedColumn, item)) {
        // Sorting on a column this scan does not have would silently shuffle it.
        this.sortedColumn = "";
        this.sortOrder = "asc";
      }
    },
    // The Industry filter offers the classification the backend uses (Finnhub labels or GICS industries); the built-in
    // Finnhub list stays when the call fails or an older backend has no such endpoint.
    async loadIndustryOptions() {
      try {
        const { data } = await axios.get("/scan/industries");
        const list = setIndustryOptions(data?.industries);
        if (!list) return;
        const select = this.scannerList.flatMap((c) => c.selects).find((s) => s.key === "finnhubindustry");
        if (select) select.list = list;
      } catch (error) {
        // Keep the built-in options.
      }
    },
    isKnownColumn(key, scan) {
      return (
        (scanDefinition[scan] || []).some((c) => c.key === key) ||
        columnGroups.some((g) => g.colItems.some((c) => c.key === key))
      );
    },

    // ---- Saved scans --------------------------------------------------------------
    // A saved scan is the URL query of the scan, minus the date (it always runs on
    // the latest day) and the page.
    savePreset() {
      const name = this.presetName.trim();
      if (!name) return;
      const query = { ...this.buildQuery() };
      delete query.date;
      delete query.page;
      const presets = this.savedScans.filter((p) => p.name !== name);
      presets.push({ name, query });
      presets.sort((a, b) => a.name.localeCompare(b.name));
      this.savedScans = presets;
      writeStorage(PRESETS_KEY, presets);
      this.presetName = "";
      this.$toast.success(this.$t("Scan saved"));
    },
    applyPreset(preset) {
      this.closeMenu(false);
      this.applyQuery(preset.query);
      this.runScan();
    },
    deletePreset(index) {
      const presets = this.savedScans.filter((_, i) => i !== index);
      this.savedScans = presets;
      writeStorage(PRESETS_KEY, presets);
    },
    // ---- AI assistant ---------------------------------------------------------------
    setAssistantOpen(open) {
      this.assistantOpen = open;
      writeStorage(ASSISTANT_KEY, open);
      if (open) this.$nextTick(() => this.$refs.assistant.focusInput());
    },
    assistantCurrentFilters() {
      return filtersFromPayload(this.buildPayload());
    },
    // The order and rows per page on screen, so a follow-up keeps or changes them (rows: 0 = all).
    assistantCurrentView() {
      return {
        sort: this.sortedColumn ? { column: this.sortedColumn, order: this.sortOrder } : null,
        rows: this.itemsPerPage,
      };
    },
    // The results for a question about them: the first rows of the page in the current order (at most 25), then the
    // results `message` names by ticker wherever they are, each with its position (n); what mapResults fills in for
    // "no data" is left out (0 where it only means that, "N/A", candle "None", the day's move without two candles)
    // and dates are yyyy-MM-dd. `filters` are the ones of the scan that produced them. Null while a scan runs.
    assistantCurrentResults(message = "") {
      if (this.scanLoader) return null;
      const page = this.paginatedProducts.slice(0, ASSISTANT_PAGE_RESULTS);
      const words = new Set(String(message).toUpperCase().match(/[A-Z0-9]+(?:[.-][A-Z0-9]+)*/g) || []);
      const named = this.sortedProducts
        .filter((row) => words.has(String(row.symbol).toUpperCase()) && !page.includes(row))
        .slice(0, ASSISTANT_MAX_RESULTS - page.length);
      const toResult = (row, n) => {
        const result = { n };
        // close comes from the latest candle: without it, there is no day's move either.
        const noMove = !row.close;
        ASSISTANT_RESULT_FIELDS.forEach((key) => {
          const v = row[key];
          if (isMissing(v) || (key === "typeChandelle" && v === "None")) return;
          // An IV rank of 0 is real (IV at its yearly low) when the stock has an IV.
          const realZero = key === "ivRank" && !!row.ivPct;
          if ((ZERO_IS_MISSING.includes(key) || key === "jourEarnings") && Number(v) === 0 && !realZero) return;
          if (noMove && MOVE_COLUMNS.includes(key)) return;
          if (DATE_COLUMNS.includes(key)) {
            const day = moment(v, "MM/DD/YYYY", true);
            if (day.isValid()) result[key] = day.format("YYYY-MM-DD");
            return;
          }
          if (typeof v === "number" || typeof v === "string") result[key] = v;
        });
        return result;
      };
      const results = [
        ...page.map((row, i) => toResult(row, this.startIndex + i + 1)),
        ...named.map((row) => toResult(row, this.sortedProducts.indexOf(row) + 1)),
      ];
      // The panel may have changed since the scan ran: the model must explain the rows with their own filters.
      const filters = this.lastRunSignature ? filtersFromPayload(JSON.parse(this.lastRunSignature)) : null;
      return { results, filters, resultCount: this.sortedProducts.length, resultStart: this.startIndex + 1 };
    },
    // Applied like a saved scan (All Stocks, every filter replaced) with the assistant's order and rows per page
    // ({ sort, rows }); returns what to restore on undo: the scan and the column group on screen.
    applyAssistantFilters(filters, view = {}) {
      // buildQuery() leaves the default rows out, and applyQuery() keeps the rows on screen without them.
      const query = { ...this.buildQuery(), rows: this.itemsPerPage === ALL_ROWS ? "all" : String(this.itemsPerPage) };
      const previous = { query, columnGroup: this.selectedColumnName };
      this.closeMenu(false);
      this.applyQuery(filtersToQuery(filters, view));
      // Only an order the assistant chose moves to the columns that show it; without one the group on screen stays.
      if (view.sort) this.showSortedColumn();
      this.showFilter = true;
      this.runScan();
      // On a narrow screen the sheet covers the results: get out of the way (the conversation is kept).
      if (window.innerWidth < WIDE_SCREEN) this.assistantOpen = false;
      return previous;
    },
    // Undo: what applyAssistantFilters returned ({ query, columnGroup }).
    restoreAssistantQuery({ query, columnGroup }) {
      this.closeMenu(false);
      this.applyQuery(query);
      if (columnGroup && this.columnTabs.some((g) => g.name === columnGroup)) this.selectedColumnName = columnGroup;
      this.runScan({ keepPage: true });
    },
    // Chip label of an assistant filter, as the filter panel would show it: "Market Cap > 50B".
    describeAssistantFilter(f) {
      const select = this.scannerList.flatMap((c) => c.selects).find((s) => s.key === f.key);
      const label = select ? this.$t(select.label) : f.key;
      const locale = this.$i18n.locale;
      if (f.option != null && select?.isDate) {
        // 2026-10-13..2026-10-17 -> "Earning Date 10/13/2026 – 10/17/2026", as the date pickers show it.
        const d = (s) => moment(s, "YYYY-MM-DD", true).format("MM/DD/YYYY");
        const range = f.option.match(/^(\d{4}-\d{2}-\d{2})?\.\.(\d{4}-\d{2}-\d{2})?$/);
        if (range && range[1] && range[2]) return `${label} ${d(range[1])} – ${d(range[2])}`;
        if (range) return range[1] ? `${label} ≥ ${d(range[1])}` : `${label} ≤ ${d(range[2])}`;
        const one = f.option.match(/^([<>]?)(\d{4}-\d{2}-\d{2})$/);
        if (one) return one[1] ? `${label} ${one[1]} ${d(one[2])}` : `${label}: ${d(one[2])}`;
      }
      if (f.option != null) {
        const option = select?.list?.find((o) => o.key === f.option);
        return `${label}: ${option ? this.$t(option.value) : f.option}`;
      }
      if (f.sign == null) return label;
      const n = (v) => formatUserNumber(v, locale);
      if (f.sign === BETWEEN) return `${label} ${n(f.value)} – ${n(f.valueMax)}`;
      return `${label} ${{ ">=": "≥", "<=": "≤" }[f.sign] || f.sign} ${n(f.value)}`;
    },
    // Chip label of the assistant's order and rows: "Sorted by Change % ↓", "10 rows per page" (rows 0 = all); null for
    // neither.
    describeAssistantView({ sort, rows } = {}) {
      const parts = [];
      if (sort) {
        const col = this.columnTabs.flatMap((g) => g.colItems).find((c) => c.key === sort.column);
        const column = col ? this.$t(col.value) : sort.column;
        parts.push(this.$t("assistant-sort-chip", { column, arrow: sort.order === "desc" ? "↓" : "↑" }));
      }
      if (rows === ALL_ROWS) parts.push(this.$t("assistant-rows-all-chip"));
      else if (rows) parts.push(this.$t("assistant-rows-chip", { count: rows }));
      return parts.length ? parts.join(" · ") : null;
    },
    // Shows a column group with the sorted column when the one on screen does not have it (a visible column first).
    showSortedColumn() {
      const key = this.sortedColumn;
      if (!key || this.selectedColumn.colItems.some((c) => c.key === key)) return;
      const groups = this.columnTabs.filter((g) => g.colItems.some((c) => c.key === key));
      const hidden = (g) => ((this.columnPrefs[g.name] || {}).hidden || []).includes(key);
      const group = groups.find((g) => !hidden(g)) || groups[0];
      if (group) this.selectedColumnName = group.name;
    },
    presetScanLabel(preset) {
      return Object.keys(scanNameMap).find((k) => scanNameMap[k] === preset.query.scan) || LEGACY_DEFAULT_SCAN;
    },

    // ---- Columns --------------------------------------------------------------------
    isColumnVisible(col) {
      if (col.key === "symbol") return true;
      const hidden = (this.columnPrefs[this.columnSetKey] || {}).hidden || [];
      return !hidden.includes(col.key);
    },
    saveColumnPrefs(prefs) {
      this.columnPrefs = { ...this.columnPrefs, [this.columnSetKey]: prefs };
      writeStorage(COLUMNS_KEY, this.columnPrefs);
    },
    changeColumn(item) {
      if (item.key === "symbol") return;
      const current = this.columnPrefs[this.columnSetKey] || {};
      const hidden = current.hidden || [];
      this.saveColumnPrefs({
        ...current,
        hidden: hidden.includes(item.key) ? hidden.filter((k) => k !== item.key) : [...hidden, item.key],
      });
    },
    moveColumn(key, step) {
      const order = this.orderedColumns.map((c) => c.key);
      const i = order.indexOf(key);
      const j = i + step;
      if (i < 1 || j < 1 || j >= order.length) return;
      [order[i], order[j]] = [order[j], order[i]];
      this.saveColumnPrefs({ ...(this.columnPrefs[this.columnSetKey] || {}), order });
    },
    resetColumns() {
      const prefs = { ...this.columnPrefs };
      delete prefs[this.columnSetKey];
      this.columnPrefs = prefs;
      writeStorage(COLUMNS_KEY, prefs);
    },
    handleSelectedColumn(item) {
      this.selectedColumnName = item.name;
    },

    // ---- Sorting and paging --------------------------------------------------------------
    sortByColumn(key) {
      if (this.sortedColumn === key) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortedColumn = key;
        this.sortOrder = "asc";
      }
      this.currentPage = 1;
    },
    ariaSort(key) {
      if (this.sortedColumn !== key) return "none";
      return this.sortOrder === "asc" ? "ascending" : "descending";
    },
    onRowsChange(e) {
      this.itemsPerPage = Number(e.target.value);
      this.currentPage = 1;
    },
    changePage(page) {
      const target = Math.min(Math.max(1, page), this.totalPages);
      if (target === this.currentPage) return;
      this.currentPage = target;
      if (this.$refs.scroller) this.$refs.scroller.scrollTop = 0;
    },
    handleScroll() {
      this.showShadow = this.$refs.scroller ? this.$refs.scroller.scrollLeft > 0 : false;
    },

    // ---- Selection -------------------------------------------------------------------------
    isChecked(item) {
      return this.checkedSymbols.has(item.symbol);
    },
    toggleSingleCheckbox(item) {
      if (this.isChecked(item)) {
        this.checkedItems = this.checkedItems.filter((x) => x.symbol !== item.symbol);
      } else {
        this.checkedItems = [...this.checkedItems, item];
      }
    },
    handleToggleAll() {
      this.checkedItems = this.allChecked ? [] : [...this.dataResult];
    },

    // ---- Cells --------------------------------------------------------------------------------
    formatPct(v, signed = false) {
      if (isMissing(v) || isNaN(Number(v))) return "—";
      const n = Number(v);
      return `${signed && n > 0 ? "+" : ""}${n.toFixed(2)}%`;
    },
    formatCompact(v) {
      if (isMissing(v) || isNaN(Number(v))) return "—";
      const n = Number(v);
      const abs = Math.abs(n);
      if (abs >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
      if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
      if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
      if (abs >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
      return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
    },
    toneClass(v) {
      const n = Number(v);
      if (isMissing(v) || isNaN(n) || n === 0) return "text-muted";
      return n < 0 ? "text-down" : "text-up";
    },
    // OCC symbol -> { type, label: "Dec 18 '26 $230" } (the Symbol column already shows the ticker); null otherwise.
    contractParts(symbol) {
      const c = parseOccSymbol(symbol);
      if (!c) return null;
      const when = formatDate(c.expiration, this.$i18n.locale, { month: "short", day: "numeric" }).replace(/\.$/, "");
      const strike = `$${c.strike.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
      return { type: c.type, label: `${when} '${c.expiration.slice(2, 4)} ${strike}` };
    },
    contractPill(type) {
      return [
        "inline-flex items-center h-5 px-1.5 rounded-md text-[11px] font-semibold",
        type === "put" ? "bg-down-bg text-down-ink" : "bg-up-bg text-up",
      ];
    },
    // "HangingMan" -> "Hanging Man"; values come straight from the scanner.
    humanize(v) {
      return String(v).replace(/([a-z])([A-Z])/g, "$1 $2");
    },
    cellText(item, key) {
      const v = item[key];
      if (key === "companyName") return isMissing(v) ? "—" : v;
      if (key === "mktcap") {
        // The scanner stores market caps in dollars (unlike the quote page's
        // Finnhub profile, which is in millions).
        if (isMissing(v) || !Number(v)) return "—";
        return `${this.formatCompact(v)}${item.marketCapCurrency ? ` ${item.marketCapCurrency}` : ""}`;
      }
      if (isMissing(v)) return "—";
      if (COMPACT_COLUMNS.includes(key)) return this.formatCompact(v);
      if (SCORE_COLUMNS.includes(key)) return Number(v).toFixed(1);
      if (PERCENT_COLUMNS.includes(key)) return this.formatPct(v, SIGNED_COLUMNS.includes(key));
      if (PRICE_COLUMNS.includes(key)) {
        if (isNaN(Number(v))) return "—";
        return `${Number(v) < 0 ? "−" : ""}$${Math.abs(Number(v)).toFixed(2)}`;
      }
      if (INTEGER_COLUMNS.includes(key)) return String(Math.round(Number(v)));
      if (key === "nextEarningDate" && EARNING_SESSIONS[item.earningHour]) {
        return `${v} · ${item.earningHour.toUpperCase()}`;
      }
      if (DATE_COLUMNS.includes(key)) return v;
      if (key === "typeChandelle") return v === "None" ? "—" : this.$t(this.humanize(v));
      if (key === "relVolume") return `${Number(v).toFixed(2)}×`;
      if (key === "ossSkewDirection") return this.$t(v);
      if (key === "typeGap") return v === "None" ? "—" : this.$t(v);
      if (key === "sector") return this.$t(v);
      if (key === "securityType") return this.$te(v) ? this.$t(v) : v;
      if (key === "weeklyOptions") return v ? "✓" : "—";
      if (key === "tdEntry") return ENTRY_LABELS[v] ? this.$t(ENTRY_LABELS[v]) : v;
      if (key === "cbSigmas") return `${Number(v).toFixed(1)}σ`;
      if (key === "cbIvHv" || key === "cbRewardRisk") return `${Number(v).toFixed(2)}×`;
      if (typeof v === "number") return v.toFixed(2);
      return v;
    },
    // Hover text spelling out an abbreviated cell (BMO / AMC / DMH after the next earnings date, the score behind a grade).
    cellTitle(item, key) {
      if (key === "nextEarningDate" && EARNING_SESSIONS[item.earningHour]) return this.$t(EARNING_SESSIONS[item.earningHour]);
      if (key === "tdGrade" && item.tdFundScore !== null) return `${this.$t("Score")} ${Math.round(item.tdFundScore)}/100`;
      if (key === "strangleIvHv60" && isMissing(item[key])) return this.$t(nullReasonKey(item.strangleIvHv60Reason));
      if (key === "cbLegs" && item.cbLegSymbols) return item.cbLegSymbols;
      return null;
    },
    cellClass(item, key) {
      if (key === "tdGrade") return GRADE_CLASSES[item[key]] || "";
      if (key === "strangleIvHv60") return Number(item[key]) > STRANGLE_IV_HV_MAX ? "text-down font-semibold" : "";
      if (key === "oss") return ["font-semibold", this.ossScoreClass(item[key])];
      if (SCORE_COLUMNS.includes(key)) return this.ossScoreClass(item[key]);
      if (SIGNED_COLUMNS.includes(key)) return this.toneClass(item[key]);
      if (key === "companyName") return "block max-w-[16rem] truncate";
      if (key === "cbLegs") return "font-medium";
      return "";
    },
    ossScoreClass(val) {
      const v = Number(val || 0);
      if (v >= 80) return "text-up";
      if (v >= 60) return "text-amber";
      return "text-muted";
    },
    isNewExtreme(item, key) {
      return key === "52w_high" ? !!item.is52W_high : !!item.is52W_low;
    },
    truncate(text) {
      return text.length > 22 ? `${text.slice(0, 22)}…` : text;
    },

    // ---- Stock details overlay ----------------------------------------------------------------------
    quoteHref(item) {
      return this.$router.resolve({ name: "Scan", params: { symbol: item.symbol }, query: this.$route.query }).href;
    },
    // A plain click opens the overlay; Ctrl/Cmd/Shift/middle click keeps the
    // browser behaviour (new tab or window) since the ticker is a real link.
    onTickerClick(e, item) {
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      this.toggaleQuoteModal(item);
    },
    toggaleQuoteModal(item) {
      if (!item?.symbol) return;
      this.quoteOpenedFromScreener = true;
      // The route watcher opens the overlay; pushing keeps a history entry so
      // the browser back button returns to the (still mounted) screener.
      this.$router
        .push({ name: "Scan", params: { symbol: item.symbol }, query: this.$route.query })
        .catch(() => { });
    },
    closeQuote() {
      const query = { ...this.$route.query };
      delete query.tab;
      if (this.quoteOpenedFromScreener) {
        this.$router.back();
      } else {
        // Deep link: there is no screener entry behind us.
        this.$router.replace({ name: "Scan", params: {}, query }).catch(() => { });
      }
    },
    onQuoteSymbolChange(symbol) {
      // Symbol changed from inside the details page: keep the URL in step
      // without adding a history entry.
      if (!symbol || symbol === this.$route.params.symbol) return;
      const query = { ...this.$route.query };
      delete query.tab;
      this.$router.replace({ name: "Scan", params: { symbol }, query }).catch(() => { });
    },
    applyRouteSymbol(symbol) {
      if (symbol) {
        this.selectedSymbol = String(symbol).toUpperCase();
        this.showQuoteModal = true;
      } else {
        this.showQuoteModal = false;
        this.selectedSymbol = null;
        this.quoteOpenedFromScreener = false;
      }
    },

    // ---- URL query string <-> scan state -------------------------------------------------------------
    // The query reflects the last executed scan (filters, scan type, date) plus
    // the view state (sort, page, rows) so a reload or a shared link reproduces it.
    buildQuery() {
      const query = {};
      const scanKey = scanNameMap[this.selectedSelector];
      // Always written: an old link without `scan` means Candlestick, not today's default.
      if (scanKey) query.scan = scanKey;
      if (!this.isUniverse && this.selectedDate && this.latestDateWithAPI) {
        const date = moment(this.selectedDate).format("YYYY-MM-DD");
        if (date !== this.latestDateWithAPI) query.date = date;
      }
      this.scannerList.forEach((category) => {
        category.selects.forEach((select) => {
          if (!this.isFilterActive(select)) return;
          if (select.isCheckbox) query[select.key] = "1";
          else if (select.isDropdown) query[select.key] = select.value;
          else if (select.isDate) {
            // 2026-10-15 (one day, as older links), >2026-10-15, or 2026-10-15..2026-10-31 with either side empty.
            const [first, last] = this.dayBounds(select);
            if (select.currentVal === BETWEEN) query[select.key] = `${first ?? ""}..${last ?? ""}`;
            else query[select.key] = select.currentVal === "=" ? first : `${select.currentVal}${first}`;
          } else if (select.isText) query[select.key] = select.value.trim().toUpperCase();
          else if (select.currentVal === BETWEEN) {
            // 20..50, or 20.. / ..50 when one side is empty.
            const [min, max] = this.numberBounds(select);
            query[select.key] = `${min ?? ""}..${max ?? ""}`;
          } else query[select.key] = `${select.currentVal}${parseUserNumber(select.value, this.$i18n.locale)}`;
        });
      });
      if (this.sortedColumn) {
        query.sort = this.sortedColumn;
        if (this.sortOrder === "desc") query.order = "desc";
      }
      if (this.currentPage > 1) query.page = String(this.currentPage);
      if (this.itemsPerPage !== DEFAULT_ROWS) query.rows = this.itemsPerPage === ALL_ROWS ? "all" : String(this.itemsPerPage);
      return query;
    },
    syncQuery() {
      if (!this.queryReady) return;
      const query = this.buildQuery();
      const current = this.$route.query || {};
      // The details overlay owns `tab`.
      if (this.$route.params.symbol && current.tab) query.tab = current.tab;
      const same =
        Object.keys(query).length === Object.keys(current).length &&
        Object.keys(query).every((k) => current[k] === query[k]);
      if (same) return;
      this.$router.replace({ name: "Scan", params: this.$route.params, query }).catch(() => { });
    },
    // Replaces the scan state with the one described by `query` (URL or saved scan).
    applyQuery(query) {
      const locale = this.$i18n.locale;
      // A bare URL opens the default view; a query without `scan` is an old Candlestick link or saved scan.
      const isBare = Object.keys(query).every((k) => k === "tab");
      const selector = Object.keys(scanNameMap).find((k) => scanNameMap[k] === query.scan)
        || (isBare ? DEFAULT_SCAN : LEGACY_DEFAULT_SCAN);
      this.sortedColumn = "";
      this.sortOrder = "asc";
      this.applyScanSelector(selector);

      if (query.date && moment(query.date, "YYYY-MM-DD", true).isValid()) {
        this.selectedDate = moment(query.date, "YYYY-MM-DD").toDate();
        this.selectedDate.setHours(20, 0, 0, 0);
        this.dateFromQuery = true;
      }

      this.scannerList = createFilters().map((category) => ({
        ...category,
        selects: category.selects.map((select) => {
          const raw = query[select.key];
          if (raw === undefined || raw === null || raw === "") return select;
          const val = Array.isArray(raw) ? String(raw[0]) : String(raw);
          if (select.isCheckbox) return { ...select, value: val === "1" || val === "true" };
          if (select.isDropdown) {
            const option = select.list.find((o) => o.key === val);
            return option ? { ...select, value: option.key, currentVal: option.value } : select;
          }
          if (select.isDate) {
            const day = (s) => {
              const m = moment(s, "YYYY-MM-DD", true);
              return m.isValid() ? m.toDate() : "";
            };
            const days = val.match(/^(\d{4}-\d{2}-\d{2})?\.\.(\d{4}-\d{2}-\d{2})?$/);
            if (days && (day(days[1]) || day(days[2]))) {
              return { ...select, currentVal: BETWEEN, value: day(days[1]), valueMax: day(days[2]) };
            }
            const one = val.match(/^([<>=]?)(\d{4}-\d{2}-\d{2})$/);
            if (!one || !day(one[2])) return select;
            return { ...select, currentVal: one[1] || "=", value: day(one[2]), valueMax: "" };
          }
          if (select.isText) return { ...select, value: val.toUpperCase() };
          const range = val.match(/^(-?\d+(?:\.\d+)?)?\.\.(-?\d+(?:\.\d+)?)?$/);
          if (range && (range[1] || range[2])) {
            const side = (s) => (s ? formatUserNumber(Number(s), locale) : "");
            return { ...select, currentVal: BETWEEN, value: side(range[1]), valueMax: side(range[2]) };
          }
          const match = val.match(/^([<>=]?)(-?\d+(?:\.\d+)?)$/);
          if (!match) return select;
          const sign = match[1] && select.list.includes(match[1]) ? match[1] : select.currentVal;
          return { ...select, currentVal: sign, value: formatUserNumber(Number(match[2]), locale) };
        }),
      }));
      // Opening the page on All Stocks starts from its default filters, like picking it in the selector.
      if (isBare && selector === UNIVERSE_SCAN) this.applyUniverseDefaults();

      if (query.sort) {
        this.sortedColumn = String(query.sort);
        this.sortOrder = query.order === "desc" ? "desc" : "asc";
      }
      const rows = query.rows === "all" || query.rows === "1000" ? ALL_ROWS : Number(query.rows);
      if (query.rows && this.rowOptions.includes(rows)) this.itemsPerPage = rows;
      const page = Number(query.page);
      this.currentPage = Number.isInteger(page) && page > 1 ? page : 1;
    },

    // ---- Language ---------------------------------------------------------------------------------------
    changeLanguage(event) {
      const next = event.target.value;
      // Re-display typed numbers in the new language's separators.
      const numbers = [];
      this.scannerList.forEach((c) => c.selects.forEach((s) => {
        if (!s.isDropdown && !s.isDate && !s.isText && !s.isCheckbox) {
          numbers.push([s, "value", parseUserNumber(s.value, this.$i18n.locale)]);
          numbers.push([s, "valueMax", parseUserNumber(s.valueMax, this.$i18n.locale)]);
        }
      }));
      this.language = next;
      this.$i18n.locale = next;
      numbers.forEach(([s, field, n]) => { if (n !== null) s[field] = formatUserNumber(n, next); });
      try {
        localStorage.setItem("lang", next);
      } catch (e) {
        // Not remembered in private mode.
      }
    },
    updateScreenWidth() {
      this.screenWidth = window.innerWidth;
    },

    // ---- Downloads and exports -----------------------------------------------------------------------------
    fileName(ext) {
      return `Oppscan_${scanNameMap[this.selectedSelector]}_${this.getDate()}.${ext}`;
    },
    saveBlob(content, type, name) {
      const blob = new Blob([content], { type });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    // Server-side CSV of the full scan, with the same filters as the table.
    async handleDownloadData() {
      this.downloading = true;
      try {
        const res = await axios.post("/getSymbolsInCsv", this.buildPayload(), {
          headers: { Authorization: "Bearer token" },
        });
        if (res?.data) {
          this.saveBlob(`\ufeff${String(res.data).trim()}\n`, "text/csv;charset=utf-8;", this.fileName("csv"));
        } else {
          this.$toast.error(this.$t("no-data"));
        }
      } catch (error) {
        this.$toast.error(this.$t("Internal server error"));
      } finally {
        this.downloading = false;
      }
    },
    handleOpenModal(val) {
      this.selectedExportType = val;
      this.showExportModal = true;
    },
    handleCloseModal() {
      this.selectedExportType = null;
      this.showExportModal = false;
    },
    handleDownload() {
      // Rows follow the table's current sort.
      const rows =
        this.selectedExportType === "symbol"
          ? this.sortedProducts.filter((item) => this.isChecked(item))
          : this.sortedProducts;
      if (!rows.length) {
        this.$toast.error(this.$t("alert-text"));
        return;
      }
      this.exportData(rows, this.selectedExport.key, this.selectedExportType === "symbol");
      this.handleCloseModal();
    },
    exportData(rows, type, symbolsOnly) {
      if (type === "traderWork") {
        const lines = rows.filter((r) => r.symbol).map((r) => `SYM,${String(r.symbol).toUpperCase()},SMART`);
        this.saveBlob(lines.join("\n"), "text/csv;charset=utf-8;", this.fileName("csv"));
        return;
      }
      // Symbol export: one column. Results export: the visible columns, in order,
      // with translated headers and raw values.
      const cols = symbolsOnly ? [{ key: "symbol", value: "Symbol" }] : this.visibleColumns;
      const header = cols.map((c) => this.$t(c.value));
      const raw = (v) => {
        if (isMissing(v)) return "";
        return typeof v === "number" && !Number.isInteger(v) ? Number(v.toFixed(4)) : v;
      };
      const body = rows.map((row) => cols.map((c) => raw(row[c.key])));
      if (type === "generic") {
        const clean = (v) => String(v).replace(/[\t\r\n]+/g, " ");
        const text = [header, ...body].map((line) => line.map(clean).join("\t")).join("\n");
        this.saveBlob(`\ufeff${text}`, "text/plain;charset=utf-8;", this.fileName("txt"));
        return;
      }
      const escape = (v) => {
        const s = String(v);
        return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csv = [header, ...body].map((line) => line.map(escape).join(",")).join("\n");
      this.saveBlob(`\ufeff${csv}`, "text/csv;charset=utf-8;", this.fileName("csv"));
    },

    // ---- Feedback ------------------------------------------------------------------------------------------------
    removeFile(index) {
      this.csvFiles.splice(index, 1);
    },
    addFiles(files) {
      const MAX_FILE_SIZE = 50 * 1024 * 1024;
      if (files.some((file) => file.size > MAX_FILE_SIZE)) {
        this.$toast.error(this.$t("alert-text2"));
        return;
      }
      if (files.length + this.csvFiles.length > 5) {
        this.$toast.error(this.$t("alert-text3"));
        return;
      }
      this.csvFiles = [...this.csvFiles, ...files];
    },
    handleFileUpload(event) {
      this.addFiles(Array.from(event.target.files || []));
      event.target.value = "";
    },
    handleDrop(event) {
      event.preventDefault();
      this.addFiles(Array.from(event.dataTransfer.files || []));
    },
    handleClose() {
      this.feedbackModal = false;
      this.formData = { subject: "", messageType: "0", message: "" };
      this.csvFiles = [];
      this.feedbackMsg = false;
      this.isError = false;
      this.submitError = "";
    },
    async handleSubmit() {
      this.submitError = "";
      this.formData.subject = this.formData.subject.trim();
      this.formData.message = this.formData.message.trim();
      this.isError =
        !this.formData.messageType ||
        this.formData.messageType === "0" ||
        !this.formData.subject ||
        !this.formData.message;
      if (this.isError) return;

      const formData = new FormData();
      formData.append("email", localStorage.getItem("email"));
      formData.append("subject", this.formData.subject);
      formData.append("messageType", this.formData.messageType);
      formData.append("message", this.formData.message);
      this.csvFiles.forEach((file) => formData.append("FileAttachment", file));

      this.isLoader = true;
      try {
        const res = await axios.post("api/Stock/submit", formData, {
          headers: { "Content-Type": "multipart/form-data", Authorization: "Bearer token" },
        });
        if (res && res.status >= 200 && res.status < 300) {
          this.feedbackMsg = true;
          this.formData = { subject: "", messageType: "0", message: "" };
          this.csvFiles = [];
        } else {
          throw new Error(`status ${res && res.status}`);
        }
      } catch (error) {
        this.isError = true;
        this.submitError = this.$t("feedback-failed");
      } finally {
        this.isLoader = false;
      }
    },
  },
  created() {
    this.applyQuery(this.$route.query || {});
  },
  mounted() {
    this.$emit("getLatestDate");
    this.loadIndustryOptions();
    window.addEventListener("resize", this.updateScreenWidth);
    document.addEventListener("mousedown", this.onDocumentPointer);
    document.addEventListener("keydown", this.onDocumentKeydown);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateScreenWidth);
    document.removeEventListener("mousedown", this.onDocumentPointer);
    document.removeEventListener("keydown", this.onDocumentKeydown);
  },
  watch: {
    latestDateWithAPI(newValue) {
      // Parse the API's "YYYY-MM-DD" value as a LOCAL date. `new Date("2026-06-23")`
      // parses as UTC midnight, which is the previous evening in ET, shifting the scan
      // one day behind the latest data. moment(value, "YYYY-MM-DD") parses in local time.
      if (!this.dateFromQuery) {
        this.selectedDate = moment(newValue, "YYYY-MM-DD").toDate();
      }
      this.dateFromQuery = false;
      // Initial scan: uses whatever filters (and page) were restored from the URL.
      this.runScan({ keepPage: true });
    },
    "$route.params.symbol": {
      immediate: true,
      handler(symbol) {
        this.applyRouteSymbol(symbol);
      },
    },
    // A shared link or a smaller result set can point past the last page.
    totalPages(total) {
      if (this.dataResult.length && this.currentPage > total) this.currentPage = total;
    },
    currentPage() { this.syncQuery(); },
    itemsPerPage() { this.syncQuery(); },
    sortedColumn() { this.syncQuery(); },
    sortOrder() { this.syncQuery(); },
  },
};
</script>

<style scoped>
.pagination li > button,
.pagination li > span {
  height: 32px;
  min-width: 32px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 500;
  color: rgb(var(--qp-ink-3));
  border-left: 1px solid rgb(var(--qp-line));
}

.pagination li:first-child > button {
  border-left: 0;
}

.pagination li > button:not(:disabled):hover {
  background: rgb(var(--qp-surface-3));
}

.pagination li > button:disabled {
  color: rgb(var(--qp-line-strong));
  cursor: not-allowed;
}

.pagination li > button.is-current {
  background: rgb(var(--qp-up-bg));
  color: rgb(var(--qp-up));
  font-weight: 600;
}

.pagination li > button:focus-visible {
  outline: 2px solid rgba(0, 170, 111, 0.4);
  outline-offset: -2px;
}

.shadow-right::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 10px;
  background: linear-gradient(to left, rgb(var(--qp-surface) / 0) 0%, rgb(var(--qp-faint) / 0.35) 100%);
  pointer-events: none;
}

.qp-btn-pulse {
  box-shadow: 0 0 0 0 rgba(0, 170, 111, 0.45);
  animation: qp-pulse 1.8s ease-out infinite;
}

@keyframes qp-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 170, 111, 0.45);
  }

  70% {
    box-shadow: 0 0 0 8px rgba(0, 170, 111, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(0, 170, 111, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .qp-btn-pulse {
    animation: none;
  }
}
</style>
